import React, { useState, useEffect } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { FaArrowLeft, FaCalendarAlt, FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const AttendanceRecordsView = ({ onNavigate }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${collegeConfig.api.baseURL}/attendance/records?month=${month}&year=${year}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { if (d.success) setRecords(d.records || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [month, year]);

  const goToPrev = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setStatusFilter('all');
  };

  const goToNext = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setStatusFilter('all');
  };

  const isCurrentOrFuture = month > now.getMonth() + 1 && year >= now.getFullYear()
    || year > now.getFullYear();

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const filtered = records.filter(r =>
    statusFilter === 'all' || r.attendance_status === statusFilter
  );

  const statusColor = (s) => {
    if (s === 'Present') return { bg: '#d1fae5', color: '#065f46', border: '#059669' };
    if (s === 'Absent') return { bg: '#fee2e2', color: '#991b1b', border: '#ef4444' };
    return { bg: '#fef3c7', color: '#92400e', border: '#f59e0b' };
  };

  const presentCount = records.filter(r => r.attendance_status === 'Present').length;
  const onDutyCount = records.filter(r => r.attendance_status === 'On Duty').length;
  const absentCount = records.filter(r => r.attendance_status === 'Absent').length;
  const filledCount = records.filter(r => r.logbook_entry_id).length;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '16px 12px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          onClick={() => onNavigate('dashboard')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#6C3483', fontWeight: 600, fontSize: 14, padding: '6px 10px', borderRadius: 8 }}
        >
          <FaArrowLeft /> Back
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>Attendance Records</h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#666' }}>Month-wise attendance from your institution</p>
        </div>
      </div>

      {/* Month Navigator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f3e8ff', border: '1px solid #d8b4fe', borderRadius: 14, padding: '12px 16px', marginBottom: 14 }}>
        <button onClick={goToPrev} style={{ background: '#6C3483', border: 'none', borderRadius: 8, color: '#fff', width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaChevronLeft />
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: '#3b0764' }}>{MONTH_NAMES[month - 1]} {year}</div>
          <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 2 }}>{records.length} record{records.length !== 1 ? 's' : ''}</div>
        </div>
        <button
          onClick={goToNext}
          disabled={isCurrentOrFuture}
          style={{ background: isCurrentOrFuture ? '#e9d5ff' : '#6C3483', border: 'none', borderRadius: 8, color: isCurrentOrFuture ? '#c4b5fd' : '#fff', width: 34, height: 34, cursor: isCurrentOrFuture ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Monthly summary pills */}
      {!loading && records.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {presentCount > 0 && <span style={{ background: '#d1fae5', color: '#065f46', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{presentCount} Present</span>}
          {onDutyCount > 0 && <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{onDutyCount} On Duty</span>}
          {absentCount > 0 && <span style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{absentCount} Absent</span>}
          <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{filledCount}/{records.length} Logbook Filled</span>
        </div>
      )}

      {/* Status filter */}
      {!loading && records.length > 0 && (
        <div style={{ display: 'flex', gap: 6, background: '#f3f4f6', borderRadius: 10, padding: 4, marginBottom: 14, width: 'fit-content' }}>
          {[['all', 'All'], ['Present', 'Present'], ['On Duty', 'On Duty'], ['Absent', 'Absent']].map(([val, label]) => (
            <button key={val} onClick={() => setStatusFilter(val)} style={{
              padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: statusFilter === val ? '#6C3483' : 'transparent',
              color: statusFilter === val ? '#fff' : '#555',
              transition: 'all 0.15s'
            }}>{label}</button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <FaSpinner style={{ fontSize: 28, color: '#6C3483', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 24px', color: '#888' }}>
          <FaCalendarAlt style={{ fontSize: 40, marginBottom: 16, color: '#ccc' }} />
          <h3 style={{ margin: '0 0 8px', color: '#555' }}>
            {records.length === 0 ? 'No attendance data' : 'No records match filter'}
          </h3>
          <p style={{ margin: 0, fontSize: 14 }}>
            {records.length === 0
              ? `No attendance records received for ${MONTH_NAMES[month - 1]} ${year}.`
              : 'Try selecting a different status filter.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(record => {
            const sc = statusColor(record.attendance_status);
            const filled = !!record.logbook_entry_id;
            return (
              <div key={record._id} style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderLeft: `4px solid ${sc.border}`,
                borderRadius: 12,
                padding: '14px 16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', flex: 1 }}>{record.lecture_name}</div>
                  <span style={{ background: sc.bg, color: sc.color, padding: '2px 10px', borderRadius: 20, fontWeight: 600, fontSize: 12, flexShrink: 0 }}>
                    {record.attendance_status}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 13, color: '#666', marginTop: 6 }}>
                  <span><FaCalendarAlt style={{ marginRight: 4, color: '#6C3483' }} />{formatDate(record.date_of_attendance)}</span>
                  {record.faculty_name && <span>Faculty: {record.faculty_name}</span>}
                </div>
                <div style={{ marginTop: 8 }}>
                  {filled ? (
                    <span style={{ background: '#d1fae5', color: '#065f46', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20 }}>
                      Logbook Entry Filled
                    </span>
                  ) : record.attendance_status === 'Absent' ? (
                    <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20 }}>
                      Absent — Entry Not Required
                    </span>
                  ) : (
                    <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20 }}>
                      Logbook Entry Pending
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AttendanceRecordsView;
